import { Box, Heading, Flex, Tooltip, useDisclosure, Stack, Spinner, Text, HStack, Button } from '@chakra-ui/react';
import { useDisableAuthUserMutation, useGetAuthUsersQuery, useResetAuthUserMutation } from '@/app/services/nucleus';
import React, { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';
import { setAuthUserId } from './userSlice';
import { createColumnHelper } from '@tanstack/react-table';
import { BsPencilSquare, BsPersonAdd, BsPersonFillCheck, BsPersonSlash } from 'react-icons/bs';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { outputRoles } from './userHelper';
import { MdLockReset } from 'react-icons/md';
import ModalComponent from '../common/ModalComponent';
import AlertComponent from '../common/AlertComponent';
import { useNotification } from '@/hooks/useNotification';
import { useRole } from '@/hooks/useRole';
import TableButton from '../common/table/TableButton';
import { usePagination } from '@/hooks/usePagination.js';
import { useServerSideSorting } from '@/hooks/useServerSideSorting.js';
import SSDataTable from '@/features/common/table/SSDataTable.jsx';
import { isRealValue } from '@/utils/helpers.js';
import SearchInput from '@/features/common/SearchInput.jsx';
import { useDebouncedSearchSync } from '@/hooks/useDebouncedSearchSync.js';

const UserManagement = () => {
  useRole('admin');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userModal = useDisclosure();
  const urlChange = useLocation();

  const tableInstanceRef = useRef(null);

  const columnHelper = createColumnHelper();
  const [currentUser, setCurrentUser] = useState(null);
  const [pageCount, setPageCount] = useState();
  const [searchText, setSearchText] = useState('');
  const [userText, setUserText] = useState('');
  const [urlParamsParsed, setUrlParamsParsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    limit,
    onPaginationChange,
    pagination: paginationState,
    offset,
  } = usePagination({
    onPageChange: (pageState) => {
      setSearchParams((params) => {
        if (Number(params.get('page')) !== Number(pageState.pageIndex)) {
          params.delete('sel');
        }
        params.set('page', pageState.pageIndex);
        params.set('limit', pageState.pageSize);
        return params;
      });
    },
  });
  const { sorting, onSortingChange, sortByField } = useServerSideSorting([{ id: 'last_name', desc: true }]);

  const {
    data: users,
    isLoading,
    isError,
    error: usersError,
  } = useGetAuthUsersQuery(
    {
      search: searchText,
      sortBy: sortByField,
      limit,
      offset,
    },
    { skip: !urlParamsParsed }, // Skip until URL params are parsed
  );

  const [
    reset,
    {
      isLoading: resetAuthUserLoading,
      isSuccess: resetAuthUserSuccess,
      isError: resetAuthUserError,
      error: resetAuthUserErrorMsg,
      reset: resetAuthUserReset,
    },
  ] = useResetAuthUserMutation();

  const [
    disable,
    {
      isLoading: disableAuthUserLoading,
      isSuccess: disableAuthUserSuccess,
      isError: disableAuthUserError,
      error: disableAuthUserErrorMsg,
      reset: disableAuthUserReset,
    },
  ] = useDisableAuthUserMutation();

  useNotification(resetAuthUserSuccess, resetAuthUserReset, resetAuthUserSuccess, 'success', userText === 'Reset' ? 'User reset' : 'User enabled');
  useNotification(disableAuthUserSuccess, disableAuthUserReset, disableAuthUserSuccess, 'success', 'User disabled');

  useDebouncedSearchSync({ searchText, onPaginationChange, limit, setSearchParams });

  useEffect(() => {
    if (isRealValue(users?.total)) {
      setPageCount(Math.ceil(users?.total / limit));
    }
  }, [users?.total, limit]);

  useEffect(() => {
    const pageState = { ...paginationState };

    for (let param of searchParams) {
      if (Array.isArray(param) && param.at(0) === 'search') {
        setSearchText(param.at(1));
      }
      if (Array.isArray(param) && param.at(0) === 'page') {
        pageState.pageIndex = Number(param.at(1));
      }
      if (Array.isArray(param) && param.at(0) === 'limit') {
        pageState.pageSize = Number(param.at(1));
      }
    }

    onPaginationChange(pageState);
    setUrlParamsParsed(true);
  }, []);

  const userColumns = [
    columnHelper.accessor('first_name', {
      header: 'First Name',
    }),
    columnHelper.accessor('last_name', {
      header: 'Last Name',
    }),
    columnHelper.accessor('email', {
      header: 'Email',
    }),
    columnHelper.accessor('roles', {
      header: 'Role',
      disableSortBy: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return outputRoles(value);
      },
    }),
    columnHelper.accessor('', {
      disableSortBy: true,
      cell: (info) => {
        const user = info.row.original;
        return (
          <>
            <HStack>
              <Tooltip hasArrow placement="top" label="Edit User">
                <Box p="0" as="span">
                  <TableButton aria-label="edit" icon={<BsPencilSquare />} onClick={(e) => handleEditUser(e, user?.id)} />
                </Box>
              </Tooltip>
              {user?.is_over_max_login_attempts && !user?.disabled_at && (
                <Tooltip hasArrow placement="top" label="Reset User">
                  <Box p="0" as="span">
                    <TableButton aria-label="reset-button" icon={<MdLockReset size="22px" />} onClick={(e) => handleResetUser(e, user?.id)} />
                  </Box>
                </Tooltip>
              )}
              {user?.disabled_at ? (
                <Tooltip hasArrow placement="top" label="Enable User">
                  <Box p="0" as="span">
                    <TableButton aria-label="enable-button" icon={<BsPersonFillCheck size="20px" />} onClick={(e) => handleEnableUser(e, user?.id)} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip hasArrow placement="top" label="Disable User">
                  <Box p="0" as="span">
                    <TableButton aria-label="disable-button" icon={<BsPersonSlash size="20px" />} onClick={(e) => handleDisableUser(e, user?.id)} />
                  </Box>
                </Tooltip>
              )}
            </HStack>
          </>
        );
      },
      header: 'Action',
    }),
  ];

  useEffect(() => {
    if (!userModal.isOpen) {
      setCurrentUser(null);
      resetAuthUserReset();
      disableAuthUserReset();
    }
  }, [userModal.isOpen]);

  const userAction = (userText) => {
    if (userText === 'Disable') {
      disable({ id: currentUser })
        .unwrap()
        .then((payload) => {
          if (payload) {
            userModal.onClose();
          }
        })
        .catch((error) => console.log('disable user rejected', error));
    } else if (userText === 'Reset' || userText === 'Enable') {
      reset({ id: currentUser })
        .unwrap()
        .then((payload) => {
          if (payload) {
            userModal.onClose();
          }
        })
        .catch((error) => console.log(`${userText} user rejected`, error));
    }
  };

  const updateUserAction = (e, id, label) => {
    e.preventDefault();
    setUserText(label);
    userModal.onOpen();
    setCurrentUser(id);
  };

  const handleDisableUser = (e, id) => {
    updateUserAction(e, id, 'Disable');
  };

  const handleResetUser = (e, id) => {
    updateUserAction(e, id, 'Reset');
  };

  const handleEnableUser = (e, id) => {
    updateUserAction(e, id, 'Enable');
  };

  const handleEditUser = (e, id) => {
    dispatch(setAuthUserId(id));
    navigate(`/user_management/${id}`, { state: { from: urlChange } });
  };

  const handleNewUser = (e) => {
    e.preventDefault();
    navigate('/user_management/new');
  };

  const handleTableLoaded = (table) => {
    tableInstanceRef.current = table;
    if (users?.items && users?.items?.length > 0) {
      users?.items.forEach((user, index) => {
        let color = '';
        if (user?.disabled_at) {
          color = 'gray.50';
        } else if (user?.is_over_max_login_attempts) {
          color = 'red.25';
        }
        table.options.meta?.addRowColor(index, color);
      });
    }
  };

  const icons = {
    Enable: <BsPersonFillCheck size="24px" />,
    Disable: <BsPersonSlash size="24px" />,
    Reset: <MdLockReset size="24px" />,
  };

  const handleClearSearchText = () => {
    setSearchText('');
    searchParams.delete('search');
    setSearchParams(searchParams);
  };

  const handleSearchTextChange = (event) => {
    if (event.target.value === '') {
      handleClearSearchText();
    } else {
      setSearchText(event.target.value);
    }
  };

  const getIcon = (text) => icons[text];

  return (
    <Box data-test-id="pact-user-management">
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        User Management
      </Heading>
      <Box p={5} width={{ base: '100%' }}>
        <HStack mt={3} mb={1} spacing={8}>
          <SearchInput isDisabled={false} searchText={searchText} dataType="text" onChange={handleSearchTextChange} onClear={handleClearSearchText} />
          <Button leftIcon={<BsPersonAdd />} onClick={handleNewUser} variant="purple" type="submit">
            Add User
          </Button>
        </HStack>
      </Box>

      {isError ? (
        <AlertComponent status="warning" title="User Management Error" description={usersError?.data?.detail ?? 'Error fetching users'} />
      ) : (
        <SSDataTable
          loading={isLoading}
          data={users?.items}
          total={users?.total ?? 0}
          columns={userColumns}
          pagination={paginationState}
          pageCount={pageCount}
          onPaginationChange={onPaginationChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
          onTableLoaded={handleTableLoaded}
        />
      )}
      <ModalComponent
        icon={getIcon(userText)}
        size="sm"
        title={`${userText} User`}
        primaryText="Yes"
        secondaryText="No"
        handleConfirm={() => userAction(userText)}
        onOpen={userModal.onOpen}
        isOpen={userModal.isOpen}
        onClose={userModal.onClose}
        isError={false}
      >
        <Stack spacing={3}>
          {resetAuthUserLoading || disableAuthUserLoading ? (
            <Flex alignItems="center" justifyContent={'center'}>
              <Spinner color="brand.900" size="lg" />
            </Flex>
          ) : (
            <Box>
              <Text>{`Are you sure you want to ${userText.toLowerCase()} this user?`}</Text>
              {(resetAuthUserError || disableAuthUserError) && (
                <AlertComponent
                  status="error"
                  title={`Error: Unable to ${userText} user`}
                  description={resetAuthUserError ? resetAuthUserErrorMsg?.data?.detail : disableAuthUserErrorMsg?.data?.detail}
                />
              )}
            </Box>
          )}
        </Stack>
      </ModalComponent>
    </Box>
  );
};

export default UserManagement;
